import { useState, FormEvent } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useRef } from 'react';
import { useInView } from 'framer-motion';
import { supabase } from '@/lib/supabase';

export default function ConversionForm() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  const [formData, setFormData] = useState({
    nombre: '',
    whatsapp: '',
    placa: '',
    tipo_vehiculo: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    const nombreTrimmed = formData.nombre.trim();
    if (nombreTrimmed.length < 3) {
      newErrors.nombre = 'El nombre debe tener al menos 3 caracteres';
    } else if (!/^[a-záéíóúñA-ZÁÉÍÓÚÑ\s]+$/.test(nombreTrimmed)) {
      newErrors.nombre = 'Solo letras y espacios';
    } else if (!/\s/.test(nombreTrimmed)) {
      newErrors.nombre = 'Ingresa nombre y apellido';
    }

    const waClean = formData.whatsapp.replace(/[\s\-()]/g, '');
    if (!waClean) {
      newErrors.whatsapp = 'El WhatsApp es obligatorio';
    } else if (waClean.length !== 10 || !waClean.startsWith('3')) {
      newErrors.whatsapp = 'Número colombiano de 10 dígitos iniciando con 3';
    }

    const placaTrimmed = formData.placa.trim().toUpperCase();
    if (!placaTrimmed) {
      newErrors.placa = 'La placa es obligatoria';
    } else if (!/^[A-Z]{3}[0-9]{2,3}$/.test(placaTrimmed)) {
      newErrors.placa = 'Formato inválido (ej: ABC123)';
    }

    if (!formData.tipo_vehiculo) {
      newErrors.tipo_vehiculo = 'Selecciona el tipo de vehículo';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      const placaNorm = formData.placa.trim().toUpperCase();
      const waNorm = formData.whatsapp.replace(/[\s\-()]/g, '');
      const partesNombre = formData.nombre.trim().split(' ');
      const nombre = partesNombre[0] || '';
      const apellidos = partesNombre.slice(1).join(' ') || 'No especificado';

      const { data: existing } = await supabase
        .from('registros')
        .select('placa, telefono')
        .or(`placa.eq.${placaNorm},telefono.eq.${waNorm}`);

      if (existing && existing.length > 0) {
        const dupErrors: Record<string, string> = {};
        for (const rec of existing) {
          if (rec.placa === placaNorm) dupErrors.placa = 'Esta placa ya está registrada';
          if (rec.telefono === waNorm) dupErrors.whatsapp = 'Este número ya está registrado';
        }
        setErrors(dupErrors);
        setErrorMessage('Algunos datos ya están registrados en el sistema.');
        setSubmitStatus('error');
        setIsSubmitting(false);
        return;
      }

      const uniqueDNI = `TEMP-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
      const { error: insertError } = await supabase.from('registros').insert([{
        nombre,
        apellidos,
        email: `${waNorm}@whatsapp.alafijaya.com`,
        telefono: waNorm,
        placa: placaNorm,
        usuario: formData.tipo_vehiculo,
        servicio: 'Registro inicial',
        mensaje: '',
        dni: uniqueDNI,
        fecha_nacimiento: '2000-01-01',
        direccion: 'Bogotá D.C.',
        codigo_postal: '110111',
        ciudad: 'Bogotá',
        provincia: 'Cundinamarca',
        situacion_laboral: 'No especificado',
        ingresos_mensuales: 'No especificado',
        gastos_mensuales: 'No especificado',
        deudas_actuales: 'No especificado',
        tipo_vivienda: 'No especificado',
        personas_cargo: 0,
        aceptacion_terminos: true,
        aceptacion_privacidad: true,
      }]);

      if (insertError) throw insertError;

      // Form submission to Readdy
      const formPayload = new URLSearchParams();
      formPayload.append('nombre', formData.nombre.trim());
      formPayload.append('whatsapp', `+57${waNorm}`);
      formPayload.append('placa', placaNorm);
      formPayload.append('tipo_vehiculo', formData.tipo_vehiculo);
      await fetch('https://readdy.ai/api/form/d79hv73ll6jbq2vddo20', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formPayload.toString(),
      }).catch(() => {});

      try {
        await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/send-notification`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            nombre: formData.nombre.trim(),
            email: `${waNorm}@whatsapp.alafijaya.com`,
            telefono: waNorm,
            placa: placaNorm,
            usuario: formData.tipo_vehiculo,
            servicio: 'Registro inicial',
            mensaje: '',
          }),
        });
      } catch (_) { /* notificación no bloquea */ }

      setSubmitStatus('success');
      setFormData({ nombre: '', whatsapp: '', placa: '', tipo_vehiculo: '' });
      setErrors({});
    } catch (err) {
      console.error(err);
      setSubmitStatus('error');
      setErrorMessage('Error de conexión. Por favor intenta nuevamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) setErrors({ ...errors, [field]: '' });
    if (submitStatus === 'error') { setSubmitStatus('idle'); setErrorMessage(''); }
  };

  return (
    <section id="registro-form" className="py-16 md:py-24 bg-[#E1E1E1]" ref={ref}>
      <div className="max-w-6xl mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          {/* Left — Copy */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-flex items-center gap-2 px-3 py-1 bg-red-500/10 border border-red-500/30 rounded-full text-red-600 text-xs font-bold tracking-wider uppercase mb-5">
              <i className="ri-fire-line"></i>
              Solo 50 cupos por semana
            </span>
            <h2 className="text-2xl md:text-4xl font-black text-[#1A2B3C] leading-tight mb-4 tracking-tight">
              Registra tu placa.{' '}
              <span className="text-[#28A745]">Participa cada jueves.</span>
            </h2>
            <p className="text-[#1A2B3C]/55 text-sm md:text-base leading-relaxed mb-6">
              Al registrar tu vehículo, participas automáticamente en el sorteo semanal vinculado al{' '}
              <strong className="text-[#1A2B3C]/75">Premio Mayor de la Lotería de Bogotá</strong>.
              Si los <strong className="text-[#1A2B3C]/75">3 últimos dígitos de tu placa</strong> coinciden en estricto orden con los 3 últimos dígitos del premio mayor, cubrimos hasta el{' '}
              <strong className="text-[#28A745]">100% del valor de tu revisión tecnomecánica</strong>.
            </p>

            {/* Mechanic box */}
            <div className="bg-white border border-[#E1E1E1] rounded-xl p-5 mb-6">
              <p className="text-[#1A2B3C] text-xs font-black uppercase tracking-wider mb-3 flex items-center gap-2">
                <i className="ri-trophy-line text-[#28A745]"></i>
                ¿Cómo funciona el sorteo?
              </p>
              <ol className="space-y-2.5">
                {[
                  { num: '1', text: 'Registra tu vehículo con tu placa en el formulario.' },
                  { num: '2', text: 'Cada jueves se juega la Lotería de Bogotá y se publica el Premio Mayor.' },
                  { num: '3', text: 'Comparamos los 3 últimos dígitos de tu placa con los 3 últimos dígitos del Premio Mayor, en estricto orden.' },
                  { num: '4', text: 'Si coinciden exactamente, cubrimos hasta el 100% del costo de tu RTM.' },
                ].map((step) => (
                  <li key={step.num} className="flex items-start gap-3">
                    <span className="w-5 h-5 flex-shrink-0 flex items-center justify-center bg-[#28A745]/10 text-[#28A745] text-[10px] font-black rounded-full mt-0.5">
                      {step.num}
                    </span>
                    <span className="text-[#1A2B3C]/65 text-xs leading-relaxed">{step.text}</span>
                  </li>
                ))}
              </ol>
              <div className="mt-4 pt-3 border-t border-[#E1E1E1] flex items-center gap-2">
                <i className="ri-information-line text-[#1A2B3C]/35 text-xs"></i>
                <p className="text-[#1A2B3C]/40 text-[10px] leading-relaxed">
                  Ejemplo: placa <strong>ABC<span className="text-[#28A745]">472</span></strong> · Premio Mayor termina en <strong className="text-[#28A745]">472</strong> → ¡Ganador!
                </p>
              </div>
            </div>

            <div className="space-y-3 mb-6">
              {[
                { icon: 'ri-shield-check-line', text: 'Diagnóstico técnico verificado con maquinaria especializada' },
                { icon: 'ri-calendar-event-line', text: 'Sorteo cada jueves vinculado a la Lotería de Bogotá' },
                { icon: 'ri-coupon-line', text: 'Bonos redimibles en llantas multimarca, sujetos a compra efectiva' },
                { icon: 'ri-lock-2-line', text: 'Datos protegidos bajo Ley 1581 de 2012' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-[#28A745]/10 rounded-lg flex-shrink-0">
                    <i className={`${item.icon} text-[#28A745] text-sm`}></i>
                  </div>
                  <span className="text-[#1A2B3C]/70 text-sm">{item.text}</span>
                </div>
              ))}
            </div>

            <div className="p-4 bg-white border border-[#E1E1E1] rounded-xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-[#1A2B3C] rounded-lg flex-shrink-0">
                  <i className="ri-building-2-line text-white text-base"></i>
                </div>
                <div>
                  <p className="text-[#1A2B3C] text-xs font-black">GESTIÓN AUTOMOTRIZ AW2020 SAS</p>
                  <p className="text-[#1A2B3C]/45 text-[10px]">NIT 901401495 · Bogotá D.C. · Red de aliados expertos</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right — Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.15 }}
          >
            <div className="bg-white border border-[#E1E1E1] rounded-2xl p-6 md:p-8">
              <AnimatePresence mode="wait">
                {submitStatus === 'success' ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    className="text-center py-8"
                  >
                    <div className="w-16 h-16 flex items-center justify-center bg-[#28A745]/15 rounded-full mx-auto mb-4">
                      <i className="ri-check-line text-[#28A745] text-3xl"></i>
                    </div>
                    <h3 className="text-[#1A2B3C] font-black text-xl mb-2">¡Registro exitoso!</h3>
                    <p className="text-[#1A2B3C]/55 text-sm mb-2 leading-relaxed">
                      Tu vehículo ha sido registrado. Participarás en el sorteo del próximo jueves con la Lotería de Bogotá.
                    </p>
                    <p className="text-[#28A745] text-xs font-bold mb-6">
                      Recuerda: si los 3 últimos dígitos de tu placa coinciden con el Premio Mayor, cubrimos hasta el 100% de tu RTM.
                    </p>
                    <button
                      onClick={() => setSubmitStatus('idle')}
                      className="px-6 py-2.5 bg-[#1A2B3C]/8 hover:bg-[#1A2B3C]/15 text-[#1A2B3C] rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap font-semibold"
                    >
                      Registrar otro vehículo
                    </button>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    onSubmit={handleSubmit}
                    className="space-y-4"
                    data-readdy-form
                    action="https://readdy.ai/api/form/d79hv73ll6jbq2vddo20"
                    method="POST"
                  >
                    <div className="mb-5">
                      <h3 className="text-[#1A2B3C] font-black text-lg">Registra tu placa y participa</h3>
                      <p className="text-[#1A2B3C]/45 text-xs mt-1">Sorteo cada jueves · Lotería de Bogotá · Hasta 100% de tu RTM</p>
                    </div>

                    {/* Nombre */}
                    <div>
                      <label className="block text-xs font-bold text-[#1A2B3C]/60 uppercase tracking-wider mb-1.5">
                        Nombre completo *
                      </label>
                      <input
                        type="text"
                        name="nombre"
                        value={formData.nombre}
                        onChange={(e) => handleChange('nombre', e.target.value)}
                        required
                        maxLength={100}
                        placeholder="Ej: Carlos Rodríguez"
                        className={`w-full px-4 py-3 bg-[#E1E1E1]/40 border ${errors.nombre ? 'border-red-400' : 'border-[#E1E1E1]'} rounded-lg text-[#1A2B3C] placeholder-[#1A2B3C]/30 focus:outline-none focus:ring-2 focus:ring-[#28A745] focus:border-transparent transition-all text-sm`}
                      />
                      {errors.nombre && <p className="text-red-500 text-xs mt-1 flex items-center gap-1"><i className="ri-error-warning-line"></i>{errors.nombre}</p>}
                    </div>

                    {/* WhatsApp */}
                    <div>
                      <label className="block text-xs font-bold text-[#1A2B3C]/60 uppercase tracking-wider mb-1.5">
                        WhatsApp *
                      </label>
                      <div className="relative">
                        <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
                          <span className="text-[10px]">🇨🇴</span>
                          <span className="text-[#1A2B3C]/50 text-sm font-bold">+57</span>
                          <span className="w-px h-4 bg-[#1A2B3C]/15"></span>
                        </div>
                        <input
                          type="tel"
                          name="whatsapp"
                          value={formData.whatsapp}
                          onChange={(e) => handleChange('whatsapp', e.target.value.replace(/[^\d\s\-()]/g, ''))}
                          required
                          maxLength={15}
                          placeholder="300 123 4567"
                          className={`w-full pl-20 pr-4 py-3 bg-[#E1E1E1]/40 border ${errors.whatsapp ? 'border-red-400' : 'border-[#E1E1E1]'} rounded-lg text-[#1A2B3C] placeholder-[#1A2B3C]/30 focus:outline-none focus:ring-2 focus:ring-[#28A745] focus:border-transparent transition-all text-sm`}
                        />
                      </div>
                      {errors.whatsapp && <p className="text-red-500 text-xs mt-1 flex items-center gap-1"><i className="ri-error-warning-line"></i>{errors.whatsapp}</p>}
                    </div>

                    {/* Placa */}
                    <div>
                      <label className="block text-xs font-bold text-[#1A2B3C]/60 uppercase tracking-wider mb-1.5">
                        Placa del vehículo *
                      </label>
                      <input
                        type="text"
                        name="placa"
                        value={formData.placa}
                        onChange={(e) => handleChange('placa', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                        required
                        maxLength={6}
                        placeholder="ABC123"
                        className={`w-full px-4 py-3 bg-[#E1E1E1]/40 border ${errors.placa ? 'border-red-400' : 'border-[#E1E1E1]'} rounded-lg text-[#1A2B3C] placeholder-[#1A2B3C]/30 focus:outline-none focus:ring-2 focus:ring-[#28A745] focus:border-transparent transition-all text-sm uppercase font-mono tracking-widest`}
                      />
                      {errors.placa && <p className="text-red-500 text-xs mt-1 flex items-center gap-1"><i className="ri-error-warning-line"></i>{errors.placa}</p>}
                    </div>

                    {/* Tipo de Vehículo */}
                    <div>
                      <label className="block text-xs font-bold text-[#1A2B3C]/60 uppercase tracking-wider mb-1.5">
                        Tipo de vehículo *
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        {[
                          { value: 'Taxi', icon: 'ri-taxi-line', label: 'Taxi' },
                          { value: 'Particular', icon: 'ri-car-line', label: 'Particular' },
                        ].map((tipo) => (
                          <button
                            key={tipo.value}
                            type="button"
                            onClick={() => handleChange('tipo_vehiculo', tipo.value)}
                            className={`py-3 rounded-lg border text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
                              formData.tipo_vehiculo === tipo.value
                                ? 'bg-[#28A745]/10 border-[#28A745] text-[#28A745]'
                                : 'bg-[#E1E1E1]/40 border-[#E1E1E1] text-[#1A2B3C]/50 hover:border-[#1A2B3C]/30'
                            }`}
                          >
                            <i className={`${tipo.icon} mr-1.5`}></i>
                            {tipo.label}
                          </button>
                        ))}
                      </div>
                      {errors.tipo_vehiculo && <p className="text-red-500 text-xs mt-1 flex items-center gap-1"><i className="ri-error-warning-line"></i>{errors.tipo_vehiculo}</p>}
                    </div>

                    {/* Error message */}
                    {submitStatus === 'error' && errorMessage && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                        <i className="ri-error-warning-line text-red-500 text-sm flex-shrink-0 mt-0.5"></i>
                        <p className="text-red-600 text-xs">{errorMessage}</p>
                      </div>
                    )}

                    {/* Legal */}
                    <p className="text-[#1A2B3C]/35 text-[10px] leading-relaxed">
                      Al registrarte, participas en el programa de beneficios de{' '}
                      <strong className="text-[#1A2B3C]/50">GESTIÓN AUTOMOTRIZ AW2020 SAS</strong>.
                      El beneficio del 100% de la RTM aplica únicamente si los 3 últimos dígitos de tu placa coinciden en estricto orden con los 3 últimos dígitos del Premio Mayor de la Lotería de Bogotá del jueves siguiente a tu registro.
                      Datos tratados conforme a la{' '}
                      <strong className="text-[#1A2B3C]/50">Ley 1581 de 2012</strong>.
                      Este programa no constituye un juego de azar.
                    </p>

                    {/* Submit */}
                    <motion.button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full py-3.5 bg-[#28A745] hover:bg-[#218838] disabled:opacity-50 disabled:cursor-not-allowed text-white font-black rounded-lg transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer text-sm tracking-wide"
                      whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                      whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                    >
                      {isSubmitting ? (
                        <><i className="ri-loader-4-line animate-spin text-lg"></i>Procesando...</>
                      ) : (
                        <><i className="ri-calendar-check-line text-lg"></i>Agendar diagnóstico gratis</>
                      )}
                    </motion.button>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
